import { AppError } from "@/lib/utils/errors";
import { locationService } from "@/modules/services/locationService";
import { useProvinceStore } from "@/modules/store/metadataStore";
import { Province } from "@/modules/types/metadata/metadata";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

const removeVietnameseTones = (str: string) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D");
};

export const useLocation = () => {
  const { provinces, setProvinces } = useProvinceStore();
  const [provincesState, setProvincesState] = useState<Province[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchProvinces = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await locationService.getProvinces();
      if (resp?.provinces) {
        setProvinces(resp.provinces);
        setProvincesState(resp.provinces);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useLocation.fetchProvinces");
      appErr.setApiAction("locationService.getProvinces");
      appErr.logError();
    } finally {
      setLoading(false);
    }
  }, [setProvinces]);

  useEffect(() => {
    if (!provinces || provinces.length === 0) {
      fetchProvinces();
    } else {
      setProvincesState(provinces);
    }
  }, [provinces, fetchProvinces]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (keyword: string) => {
        try {
          setLoading(true);
          const resp = await locationService.getProvinces(keyword);
          if (resp?.provinces && resp.provinces.length > 0) {
            setProvincesState(resp.provinces);
          } else if (provinces && provinces.length > 0) {
            // Fallback: If API returns 0 results (e.g. unaccented search), match locally against cached provinces
            const normKeyword = removeVietnameseTones(keyword).toLowerCase();
            const matched = provinces.filter((p) => {
              const fullNameNorm = removeVietnameseTones(p.fullName || "").toLowerCase();
              const nameNorm = removeVietnameseTones(p.name || "").toLowerCase();
              return (
                fullNameNorm.includes(normKeyword) ||
                nameNorm.includes(normKeyword) ||
                (p.nameEn && p.nameEn.toLowerCase().includes(normKeyword))
              );
            });
            setProvincesState(matched);
          } else {
            setProvincesState([]);
          }
        } catch (err) {
          const appErr = new AppError(err);
          appErr.setAction("useLocation.searchProvinces");
          appErr.setApiAction("locationService.getProvinces");
          appErr.logError();
        } finally {
          setLoading(false);
        }
      }, 400),
    [provinces]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const resetProvinces = useCallback(() => {
    debouncedSearch.cancel();
    if (provinces && provinces.length > 0) {
      setProvincesState(provinces);
    } else {
      fetchProvinces();
    }
  }, [debouncedSearch, fetchProvinces, provinces]);

  const searchProvinces = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) {
        resetProvinces();
        return;
      }
      setLoading(true);
      debouncedSearch(trimmed);
    },
    [debouncedSearch, resetProvinces]
  );

  return {
    provinces: provincesState,
    loading,
    fetchProvinces,
    searchProvinces,
    resetProvinces,
  };
};
